import React, { useState } from 'react';
import * as Sentry from "@sentry/react";
import { useAuth } from '../../../contexts/AuthContext';
import { useFollow } from '../../../hooks/useProfile';
import { toast } from 'react-toastify';
import { UserPlus, Check } from 'lucide-react';
import { CommunityError } from "../../../errors/sentryErrors";
import { trackEvent } from "../../../utils/analytics";
import { BANNED_MESSAGE } from "../../../utils/roles";

const FollowButton = ({ handle, initialIsFollowing, onFollowChange, className = "" }) => {
  const { user, isBanned } = useAuth();
  const { follow, unfollow } = useFollow();
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Do not show for self
  if (user && (user.handle === handle || user.name === handle)) {
    return null;
  }

  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.info("Please log in to follow creators.");
      // Ideally open login modal here if implemented, else toast
      return;
    }

    if (loading || isBanned) return;

    // Optimistic UI
    const newValue = !isFollowing;
    setIsFollowing(newValue);
    if (onFollowChange) onFollowChange(newValue ? 1 : -1);
    setLoading(true);

    try {
      if (newValue) {
        await follow(handle);
        toast.success(`You are now following ${handle}`, { autoClose: 2000, icon: "👤" });
      } else {
        await unfollow(handle);
      }
      trackEvent(newValue ? "user_followed" : "user_unfollowed", { target_handle: handle });
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      // Rollback on failure
      setIsFollowing(!newValue);
      if (onFollowChange) onFollowChange(!newValue ? 1 : -1);
      
      if (err.status === 403 && err.code === "USER_BANNED") {
        toast.error(BANNED_MESSAGE);
      } else if (err.status === 429 && err.retryAfter) {
        toast.error(`You're doing that too fast. Try again in ${err.retryAfter}s.`);
      } else {
        toast.error("Failed to update follow status.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (isFollowing) {
    return (
      <button
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={loading || isBanned}
        className={`flex items-center justify-center px-4 py-1.5 rounded-full font-bold text-xs transition-colors border ${isBanned ? 'opacity-50 cursor-not-allowed ' : ''}${
          isHovered
            ? "border-red-500 text-red-600 bg-red-50 hover:bg-red-100"
            : "border-gray-200 text-gray-700 bg-white"
        } ${className}`}
      >
        {isHovered ? "Unfollow" : <><Check size={14} className="mr-1" /> Following</>}
      </button>
    );
  }

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`flex items-center justify-center bg-gray-900 hover:bg-gray-800 text-white px-4 py-1.5 rounded-full font-bold text-xs transition-colors ${className}`}
    >
      <UserPlus size={14} className="mr-1" /> Follow
    </button>
  );
};

export default FollowButton;
